import { useAuth } from "../redux/ducks/auth";
import { isBooleanTrue, isListEmpty, isListNotEmpty, isObjectNotEmpty, isStringEmpty } from "./objectUtils";

const getCoveredEntityUser = (authContext, coveredEntityId) => {
    const { coveredEntityUsers } = authContext;

    return coveredEntityUsers.find(i => i.coveredEntityId === coveredEntityId);
};

export const hasCoveredEntityPermissions = (authContext, coveredEntityId, roles) => {
    if (hasAdminPermissions(authContext)) {
        return true;
    }

    if (isStringEmpty(coveredEntityId)) return false;

    const coveredEntityUser = getCoveredEntityUser(authContext, coveredEntityId);
    if (isObjectNotEmpty(coveredEntityUser)) {
        if (isListEmpty(roles)) return true;

        return coveredEntityUser.roles.some(r => roles.includes(r));
    }

    return false;
};

const getAffiliateUser = (authContext, affiliateId) => {
    const { affiliateUsers } = authContext;

    return affiliateUsers.find(i => i.affiliateId === affiliateId);
};

export const hasAffiliatePermissions = (authContext, affiliateId, roles) => {
    if (hasAdminPermissions(authContext) || hasMarketingManagerPermissions(authContext)) {
        return true;
    }

    if (isStringEmpty(affiliateId)) return false;

    const affiliateUser = getAffiliateUser(authContext, affiliateId);
    if (isObjectNotEmpty(affiliateUser)) {
        if (isListEmpty(roles)) return true;

        return affiliateUser.roles.some(r => roles.includes(r));
    }

    return false;
};

const getPartnerUser = (authContext, partnerId) => {
    const { partnerUsers } = authContext;

    return partnerUsers.find(i => i.partnerId === partnerId);
};

export const hasPartnerPermissions = (authContext, partnerId, roles) => {
    if (hasAdminPermissions(authContext) || hasMarketingManagerPermissions(authContext)) {
        return true;
    }

    if (isStringEmpty(partnerId)) return false;

    const partnerUser = getPartnerUser(authContext, partnerId);
    if (isObjectNotEmpty(partnerUser)) {
        if (isListEmpty(roles)) return true;

        return partnerUser.roles.some(r => roles.includes(r));
    }

    return false;
};

export const hasAllStaffPermissions = (authContext) => {
    const { isAdmin, isSuperAdmin, isHelpStaff, isStaffManager, isMarketingManager, isRep } = authContext;

    if (isBooleanTrue(isRep) || isBooleanTrue(isHelpStaff) || isBooleanTrue(isStaffManager) || isBooleanTrue(isMarketingManager) || isBooleanTrue(isAdmin) || isBooleanTrue(isSuperAdmin)) {
        return true;
    }

    return false;
};

export const hasHelpStaffPermissions = (authContext) => {
    const { isAdmin, isSuperAdmin, isHelpStaff, isStaffManager } = authContext;

    if (isBooleanTrue(isHelpStaff) || isBooleanTrue(isStaffManager) || isBooleanTrue(isAdmin) || isBooleanTrue(isSuperAdmin)) {
        return true;
    }

    return false;
};

export const hasRepPermissions = (authContext) => {
    const { isAdmin, isSuperAdmin, isRep, isStaffManager, isMarketingManager } = authContext;

    if (isBooleanTrue(isRep) || isBooleanTrue(isMarketingManager) || isBooleanTrue(isAdmin) || isBooleanTrue(isSuperAdmin) || isBooleanTrue(isStaffManager)) {
        return true;
    }

    return false;
};

export const hasStaffManagerPermissions = (authContext) => {
    const { isAdmin, isSuperAdmin, isStaffManager } = authContext;

    if (isBooleanTrue(isAdmin) || isBooleanTrue(isSuperAdmin) || isBooleanTrue(isStaffManager)) {
        return true;
    }

    return false;
};

export const hasMarketingManagerPermissions = (authContext) => {
    const { isAdmin, isSuperAdmin, isMarketingManager } = authContext;

    if (isBooleanTrue(isAdmin) || isBooleanTrue(isSuperAdmin) || isBooleanTrue(isMarketingManager)) {
        return true;
    }

    return false;
};

// funnel authoring: the designer, and the admins who already author. a designer is not staff and appears in no other
// helper here - each of those opens another customer's records, and hasRepPermissions is the impersonation gate
export const hasDesignerPermissions = (authContext) => {
    const { isAdmin, isSuperAdmin, isDesigner } = authContext;

    if (isBooleanTrue(isAdmin) || isBooleanTrue(isSuperAdmin) || isBooleanTrue(isDesigner)) {
        return true;
    }

    return false;
};

export const hasRepPerformancePermissions = (authContext) => {
    const { isAdmin, isSuperAdmin, isStaffManager, isMarketingManager } = authContext;

    if (isBooleanTrue(isAdmin) || isBooleanTrue(isSuperAdmin) || isBooleanTrue(isStaffManager) || isBooleanTrue(isMarketingManager)) {
        return true;
    }

    return false;
};

export const hasAdminPermissions = (authContext) => {
    const { isAdmin, isSuperAdmin } = authContext;

    if (isBooleanTrue(isAdmin) || isBooleanTrue(isSuperAdmin)) {
        return true;
    }

    return false;
};

export const hasOwnerPermissions = (authContext) => {
    const { isOwner, isSuperAdmin } = authContext;

    if (isBooleanTrue(isOwner) || isBooleanTrue(isSuperAdmin)) {
        return true;
    }

    return false;
};

export const hasSuperAdminPermissions = (authContext) => {
    const { isSuperAdmin } = authContext;

    if (isBooleanTrue(isSuperAdmin)) {
        return true;
    }

    return false;
};

export const hasSystemAdminPermissions = (authContext) => {
    const { isSuperAdmin } = authContext;

    if (isBooleanTrue(isSuperAdmin) && authContext?.currentAuthUser?.email === 'admin@readyrx.com') {
        return true;
    }

    return false;
};

export const hasUserAccount = (authContext) => {
    const { currentAuthUser } = authContext;

    if (isObjectNotEmpty(currentAuthUser)) {
        return true;
    }

    return false;
};

export const hasUserIdMatch = (authContext, userId) => {
    const { currentAuthUserId } = authContext;

    if (hasAdminPermissions(authContext)) {
        return true;
    }

    if (userId === currentAuthUserId) {
        return true;
    }

    return false;
};

// Strict ownership, and deliberately not hasUserIdMatch above: that one answers "may
// this account act on this record" and so passes for any admin. This answers "is this
// account the record's owner", which is what to ask before writing a field the owner
// alone should decide — an admin impersonating a patient has to fail it. Empty on
// either side fails too: not knowing who is signed in is not a match.
export const isSignedInUser = (authContext, userId) => {
    const { currentAuthUserId } = authContext;

    if (isStringEmpty(userId) || isStringEmpty(currentAuthUserId)) {
        return false;
    }

    return userId === currentAuthUserId;
};

export const hasCoveredEntityAccess = (authContext, roles) => {
    const { coveredEntityUsers } = authContext;

    if (hasAdminPermissions(authContext)) {
        return true;
    }

    if (isListNotEmpty(coveredEntityUsers)) {
        if (isListEmpty(roles)) return true;

        return coveredEntityUsers.some(l => l.roles.some(r => roles.includes(r)));
    }

    return false;
};

export const CoveredEntityView = ({ coveredEntityId, roles, children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasCoveredEntityPermissions(authContext, coveredEntityId, roles)) {
        return children;
    } else {
        return null;
    }
};

export const hasAffiliateAccess = (authContext, roles) => {
    const { affiliateUsers } = authContext;

    if (hasAdminPermissions(authContext)) {
        return true;
    }

    if (isListNotEmpty(affiliateUsers)) {
        if (isListEmpty(roles)) return true;

        return affiliateUsers.some(l => l.roles.some(r => roles.includes(r)));
    }

    return false;
};

export const AffiliateView = ({ affiliateId, roles, children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasAffiliatePermissions(authContext, affiliateId, roles)) {
        return children;
    } else {
        return null;
    }
};

export const hasPartnerAccess = (authContext, roles) => {
    const { partnerUsers } = authContext;

    if (hasAdminPermissions(authContext)) {
        return true;
    }

    if (isListNotEmpty(partnerUsers)) {
        if (isListEmpty(roles)) return true;

        return partnerUsers.some(l => l.roles.some(r => roles.includes(r)));
    }

    return false;
};

export const PartnerView = ({ partnerId, roles, children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasPartnerPermissions(authContext, partnerId, roles)) {
        return children;
    } else {
        return null;
    }
};

export const RepView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasRepPermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};

export const StaffManagerView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasStaffManagerPermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};

export const MarketingManagerView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasMarketingManagerPermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};

export const DesignerView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasDesignerPermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};

export const OwnerView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasOwnerPermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};

export const AdminView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasAdminPermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};

export const SuperAdminView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasSuperAdminPermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};

export const SystemAdminView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasSystemAdminPermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};

export const RepPerformanceView = ({ children }) => {
    //#region useContext

    const authContext = useAuth();

    //#endregion

    if (hasRepPerformancePermissions(authContext)) {
        return children;
    } else {
        return null;
    }
};
